<template>
  <div class="ppv-root">
    <div class="ppv-main-container">
      <div class="ppv-page-header">
        <div class="ppv-page-header-top">
          <button class="ppv-back-btn" @click="goBack">
            <v-icon class="ppv-back-icon">{{ icons.mdiArrowLeft }}</v-icon>
          </button>
          <h1 class="ppv-page-title">{{ $t('ppv.page.title') }}</h1>
        </div>

        <div class="ppv-meta-bar">
          <div class="ppv-meta-item" @click="trackHeaderContext('period')">
            <v-icon class="ppv-meta-icon">{{ icons.mdiCalendarMonth }}</v-icon>
            <span class="ppv-meta-label">{{ $t('ppv.page.period') }}</span>
            <span class="ppv-meta-value">{{ periodDisplay }}</span>
          </div>

          <div class="ppv-meta-item" @click="trackHeaderContext('device')">
            <v-icon class="ppv-meta-icon">{{ icons.mdiCellphoneWireless }}</v-icon>
            <span class="ppv-meta-label">{{ $t('ppv.page.device') }}</span>
            <span class="ppv-meta-value">{{ deviceDisplay }}</span>
          </div>

          <div class="ppv-meta-item" @click="trackHeaderContext('user')">
            <v-icon class="ppv-meta-icon">{{ icons.mdiAccount }}</v-icon>
            <span class="ppv-meta-label">{{ $t('ppv.page.user') }}</span>
            <span class="ppv-meta-value">{{ userDisplay }}</span>
          </div>

          <div class="ppv-meta-spacer" />

          <div class="ppv-meta-updated">
            <span class="ppv-updated-text">
              {{ $t('ppv.page.updated') }} {{ lastUpdatedDisplay }}
            </span>
            <button class="ppv-refresh-btn" data-analytics-skip @click="refreshData">
              <v-icon class="ppv-refresh-icon" :class="{ 'ppv-spinning': isRefreshing }">
                {{ icons.mdiSync }}
              </v-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="ppv-info-banner" @click="trackDisclaimerInteraction">
        <v-icon class="banner-icon">{{ icons.mdiInformationOutline }}</v-icon>
        <span class="banner-text">{{ $t("ppv.insights.banner_info") }}</span>
      </div>
      <ppv-filter-panel
        :filters.sync="filters"
        :visits="filteredVisits"
        :date-ranges="dateRanges"
        :track-options="trackOptions"
        :triage-options="triageOptions"
        :track-map="trackMap"
        :all-disease-categories="allDiseaseCategories"
        :all-visits.sync="allVisits"
      />
      <ppv-stats-header
        :visits="filteredVisits"
      />
      <ppv-insights-table
        :visits="filteredVisits"
        :all-mrx-conditions="allMrxConditions"
        :track-options="trackOptions"
      />
    </div>
  </div>
</template>

<script>
import icons from '@/mixins/icons'
import { EVENTS, PVI_PANEL_NAME } from '@/plugins/analytics/events'
import { createActiveSessionTimer } from '@/plugins/analytics/helpers'
import PpvStatsHeader from '@/components/PastPatientVisits/PpvStatsHeader'
import PpvFilterPanel from '@/components/PastPatientVisits/PpvFilterPanel'
import PpvInsightsTable from '@/components/PastPatientVisits/PpvInsightsTable'
import { useVisitFilters } from '@/components/PastPatientVisits/composables/useVisitFilters'
import { useSafeLayout } from '@/components/PastPatientVisits/composables/useSafeLayout'
import { PVI_ENCOUNTER_MODES } from '@/utils/patientVisitInsightsUtils'
import { getAllRecordsByIndex, STORES } from "../../utils/indexedDBUtils";
import { buildEncounterDetails } from '@/utils/encounterDetails'
import { conditions as mrxConditions } from '@/utils/mrxEngine'
import { usePreferenceStore } from '@/stores/preference'
import { mapState } from 'pinia'

export default {
  name: 'PastPatientVisits',
  components: {
    PpvStatsHeader,
    PpvFilterPanel,
    PpvInsightsTable
  },
  mixins: [icons],
  data() {
    return {
      allVisits: [],
      allMrxConditions: [],
      allDiseaseCategories: [],
      filters: {
        dateRange: 'month',
        dateFrom: '',
        dateTo: '',
        conditionMode: 'user_selected',
        tracks: [],
        triages: [],
        diseaseCategories: [],
      },
      lastUpdated: new Date(),
      isRefreshing: false,
      sessionTimer: null,
      interactionCount: 0,
    }
  },
  computed: {
    dateRanges() {
      return this.getDateRangesHelper(this.icons)
    },
    trackOptions() {
      return this.getTrackOptionsHelper(this.icons)
    },
    trackMap() {
      return this.getTrackMapHelper(this.trackOptions)
    },
    triageOptions() {
      return this.getTriageOptionsHelper(this.icons)
    },
    filteredVisits() {
      return this.applyFiltersHelper(this.allVisits, this.filters)
    },
    periodDisplay() {
      const range = this.filters.dateRange
      const now = new Date()

      if (range === 'specific' && this.filters.dateFrom && this.filters.dateTo) {
        return this.formatDate(new Date(this.filters.dateFrom)) + ' - ' + this.formatDate(new Date(this.filters.dateTo))
      }

      if (range === 'today') {
        const d = this.formatDate(now)

        return d + ' - ' + d
      }

      if (range === 'week') {
        const start = new Date(now)
        start.setDate(now.getDate() - now.getDay())

        return this.formatDate(start) + ' - ' + this.formatDate(now)
      }

      if (range === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)

        return this.formatDate(start) + ' - ' + this.formatDate(now)
      }

      if (range === 'lastMonth') {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)

        return this.formatDate(start) + ' - ' + this.formatDate(end)
      }

      return '—'
    },
    deviceDisplay() {
      try {
        const id = this.$getDeviceId()

        if (id) {
          const short = id.replace(/^device_/, '').substring(0, 12).toUpperCase()

          return 'DEVICE-' + short
        }
      } catch (e) { /* ignore */ }

      return '—'
    },
    userDisplay() {
      try {
        const userId = this.getResponse('user_id', 'number').value || null

        if (userId) {
          return 'USR-' + userId
        }
      } catch (e) { /* ignore */ }

      return '—'
    },
    lastUpdatedDisplay() {
      const d = this.lastUpdated
      const month = d.getMonth() + 1
      const day = d.getDate()
      const year = d.getFullYear()
      let hours = d.getHours()
      const mins = String(d.getMinutes()).padStart(2, '0')
      const ampm = hours >= 12 ? 'PM' : 'AM'
      hours = hours % 12 || 12

      return `${month}/${day}/${year} - ${hours}:${mins} ${ampm}`
    },
    ...mapState(usePreferenceStore, ['getResponse']),
  },
  created() {
    const { getDateRanges, getTrackOptions, getTriageOptions, getTrackMap, applyFilters } = useVisitFilters()
    const { performSafeLayoutChange } = useSafeLayout(this)
    this.getDateRangesHelper = getDateRanges
    this.getTrackOptionsHelper = getTrackOptions
    this.getTriageOptionsHelper = getTriageOptions
    this.getTrackMapHelper = getTrackMap
    this.applyFiltersHelper = applyFilters
    this.performSafeLayoutBridge = performSafeLayoutChange
  },
  async mounted() {
    this.performSafeLayoutBridge(() => {
      document.body.classList.add('ppv-page-active')
    })

    this.sessionTimer = createActiveSessionTimer()
    this.sessionTimer.start()
    this.$track(EVENTS.PV_PAGE_VIEW, {
      panel_name: PVI_PANEL_NAME,
      tab: 'insights',
      source: this.$route.query?.source || 'menu',
    })
    this.$track(EVENTS.PV_DISCLAIMER_SHOWN, {
      panel_name: PVI_PANEL_NAME,
    })

    await this.loadClinicalConditions()
    await this.loadPastPatientVisitsData()
  },
  beforeDestroy() {
    this.performSafeLayoutBridge(() => {
      document.body.classList.remove('ppv-page-active')
    })

    const durationSeconds = this.sessionTimer ? this.sessionTimer.stop() : 0
    this.$track(EVENTS.PV_PAGE_EXIT, {
      panel_name: PVI_PANEL_NAME,
      session_duration_seconds: durationSeconds,
      interaction_count: this.interactionCount,
    })
  },
  methods: {
    async loadClinicalConditions() {
      try {
        const conditions = await mrxConditions()
        this.allMrxConditions = conditions || []
        // eslint-disable-next-line max-len
        this.allDiseaseCategories = [...new Set((conditions || []).flatMap(c => c.categories || []))].sort()
      } catch (e) {
        console.warn('[PastPatientVisits] Could not load MRX conditions:', e)
      }
    },
    async loadPastPatientVisitsData() {
      try {
        const userDetails = buildEncounterDetails()?.user || {}
        if (!userDetails?.hr_id) {
          return Promise.reject(new Error("Device ID is missing"))
        }

        const patientVisitsData = userDetails?.id ? await getAllRecordsByIndex(
            STORES.PATIENT_VISIT_INSIGHTS,
            'deviceId_userId',
            [ userDetails?.hr_id, userDetails?.id ])
            : await getAllRecordsByIndex(
                STORES.PATIENT_VISIT_INSIGHTS,
                'deviceId',
                userDetails?.hr_id)
        this.allVisits = patientVisitsData.map(data => {
          let riskData = []
          let noRiskData = []

          data?.conditions?.forEach(condition => {
            if (condition.triage_recommendation === 'none') {
              noRiskData.push(condition.name)
            } else if (condition.triage_recommendation !== 'inconclusive') {
              riskData.push(condition)
            }
          })

          return {
            ...data,
            encounterMode: data.encounterMode || PVI_ENCOUNTER_MODES.PWA,
            conditions: riskData,
            detailData: {
              noRiskFound: noRiskData,
            },
          }
        })
      } catch (e) {
        console.error('[PastPatientVisits] Load Past Patient Visits data error:', e)
      }
    },
    formatDate(date) {
      // eslint-disable-next-line max-len
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear()
    },
    goBack() {
      this.$router.go(-1)
    },
    async refreshData() {
      const isOnline = navigator.onLine
      this.isRefreshing = true
      this.interactionCount++

      let result = 'success'
      try {
        await this.loadClinicalConditions()
        await this.loadPastPatientVisitsData()
        this.lastUpdated = new Date()
      } catch (e) {
        result = isOnline ? 'load_error' : 'offline_fail'
      }

      this.$track(EVENTS.PV_REFRESH_CLICKED, {
        panel_name: PVI_PANEL_NAME,
        online_status: isOnline,
        result,
      })

      setTimeout(() => { this.isRefreshing = false }, 600)
    },
    trackDisclaimerInteraction() {
      this.interactionCount++
      this.$track(EVENTS.PV_DISCLAIMER_INTERACTED, {
        panel_name: PVI_PANEL_NAME,
      })
    },
    trackHeaderContext(contextType) {
      this.interactionCount++
      this.$track(EVENTS.PV_HEADER_CONTEXT_CLICKED, {
        panel_name: PVI_PANEL_NAME,
        context_type: contextType,
      })
    },
  },
}
</script>

<style scoped>
.ppv-root {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  overflow-x: hidden;
  background-color: #f4f6f9;
  min-height: 100vh;
  padding: 0 20px 100px;
  font-family: 'Inter', system-ui, sans-serif;
  color: #1a2340;
  box-sizing: border-box;
}
.ppv-main-container {
  max-width: 1300px;
  margin: 0 auto;
}
.ppv-page-header {
  padding: 20px 0 0;
}
.ppv-page-header-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.ppv-back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: background 0.15s;
}
.ppv-back-btn:hover {
  background: rgba(0,0,0,0.06);
}
.ppv-back-icon {
  font-size: 22px !important;
  color: #1a2340 !important;
}
.ppv-page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a2340;
  margin: 0;
  flex: 1;
}
.ppv-badge-beta {
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ppv-meta-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  padding: 10px 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.ppv-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.ppv-meta-icon {
  font-size: 15px !important;
  color: #90a4ae !important;
}
.ppv-meta-label {
  font-size: 10px;
  font-weight: 700;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ppv-meta-value {
  font-size: 12px;
  font-weight: 600;
  color: #1a2340;
}
.ppv-meta-spacer {
  flex: 1;
}
.ppv-meta-updated {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.ppv-updated-text {
  font-size: 12px;
  color: #e53935;
  font-weight: 500;
}
.ppv-refresh-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: background 0.15s;
}
.ppv-refresh-btn:hover {
  background: rgba(0,0,0,0.06);
}
.ppv-refresh-icon {
  font-size: 18px !important;
  color: #90a4ae !important;
  transition: transform 0.3s;
}
.ppv-spinning {
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.ppv-info-banner {
  background-color: #fff9e6;
  border: 1px solid #fecb2e;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.banner-icon {
  color: #fecb2e !important;
  font-size: 18px !important;
}
.banner-text {
  color: #856404;
  font-size: 13px;
  font-weight: 500;
}
@media (max-width: 768px) {
  .ppv-meta-bar {
    gap: 12px;
    padding: 10px 14px;
  }
  .ppv-meta-item {
    flex: 1 1 45%;
  }
  .ppv-meta-spacer {
    display: none;
  }
  .ppv-meta-updated {
    flex: 1 1 100%;
    justify-content: flex-end;
    margin-top: 4px;
  }
}
</style>
